/**
 * JobPicker — choose a job by typing (paging 4/4). The ledger forms used to
 * offer every lead in one dropdown, built from the whole lead list; that list
 * stopped at 1,000 rows and a dropdown of 1,560 jobs could not be used anyway.
 *
 * Type a job number, name, phone or email: the same server search as All
 * Clients (GET /leads/search), 8 at a time. The chosen job shows as
 * "#4001 · Jane Smith" with a clear button. Value is the lead id, '' for none.
 */

import React, { useEffect, useState } from 'react';
import { CRM } from '../lib/theme';
import { useApiClient } from '../hooks/useApi';
import { useLeadLabels } from '../contexts/LeadsContext';
import { formatJobNo } from '../lib/jobNumber';

const C = { ...CRM };

export const jobText = (l: any): string => {
  const name = `${l?.first_name ?? ''} ${l?.last_name ?? ''}`.trim();
  return `${formatJobNo(l)}${name ? ` · ${name}` : ''}`;
};

export function JobPicker({ value, onChange, style }: { value: string; onChange: (leadId: string) => void; style?: React.CSSProperties }) {
  const api = useApiClient();
  const labels = useLeadLabels(value ? [value] : []);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<any[]>([]);

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) { setRows([]); return; }
    let alive = true; // a slower, older response must not replace a newer one
    const t = setTimeout(() => {
      api.searchLeads({ q: query, page: 1, page_size: 8 })
        .then((r) => { if (alive) setRows(r.data?.rows ?? []); })
        .catch(() => { if (alive) setRows([]); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [api, q]);

  if (value) {
    const l = labels.get(value);
    return (
      <div style={{ ...style, display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: C.text }}>{l ? jobText(l) : 'Loading…'}</span>
        <button type="button" onClick={() => { onChange(''); setQ(''); }} aria-label="Clear job"
          style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: '13px', padding: 0 }}>✕</button>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Type a job #, name or phone…"
        style={style}
      />
      {open && q.trim().length >= 2 && (
        <div style={{ position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, background: C.panel, border: `1px solid ${C.border}`, borderRadius: '7px', zIndex: 10, maxHeight: '220px', overflowY: 'auto' }}>
          {rows.length === 0 ? (
            <div style={{ padding: '8px 10px', fontSize: '11px', color: C.dim }}>No matching jobs.</div>
          ) : rows.map((l) => (
            <div key={l.id} onMouseDown={(e) => { e.preventDefault(); onChange(String(l.id)); setOpen(false); setQ(''); }}
              style={{ padding: '7px 10px', fontSize: '12px', color: C.text, cursor: 'pointer', borderBottom: `1px solid ${C.panel2}` }}>
              {jobText(l)} <span style={{ color: C.dim }}>· {l.stage}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
